import { useQuery } from '@tanstack/react-query';

import { sessionQueryOptions } from './queryOptions';
import type { ServerSession } from './types';

export type SessionState =
  | ServerSession
  | { readonly status: 'pending'; readonly paused: boolean }
  | { readonly status: 'failed'; readonly error: unknown; readonly retry: () => void };

const CHECKING: SessionState = { status: 'pending', paused: false };

const WAITING_FOR_CONNECTION: SessionState = { status: 'pending', paused: true };

/**
 * Who is signed in on this browser, as the server answers it. Answer first:
 * once the server has answered, a failed re-read keeps that answer.
 */
export const useServerSession = (): SessionState => {
  const { data, error, isError, fetchStatus, refetch } = useQuery(sessionQueryOptions());
  if (data !== undefined) return data;
  if (isError) return { status: 'failed', error, retry: () => void refetch() };
  return fetchStatus === 'paused' ? WAITING_FOR_CONNECTION : CHECKING;
};

/**
 * What this tab already knows about the session, without asking: for screens
 * that only follow it. A signed-out visit then costs no request — the server
 * answers a signed-out browser with a 401, which every browser logs.
 */
export const useKnownSession = (): ServerSession | undefined => useQuery({ ...sessionQueryOptions(), enabled: false }).data;
